(()=>{
  const current = document.currentScript;
  if (!current) return;

  const DB_NAME = 'choices-fitting-room-cache';
  const STORE_NAME = 'preview-images';
  const CACHE_TTL = 24 * 60 * 60 * 1000;
  const VIEWS = ['front', 'three_quarter', 'back'];
  const createdObjectUrls = new Set();

  const assetUrl = (filename) => {
    const url = new URL(current.src);
    url.pathname = url.pathname.replace(/choices-try-on\.js$/, filename);
    url.search = '';
    return url.href;
  };

  const ensure = (root, selector, tag = 'div', attrs = {}) => {
    let el = root.querySelector(selector);
    if (!el) {
      el = document.createElement(tag);
      Object.entries(attrs).forEach(([key, value]) => {
        if (key === 'text') el.textContent = value;
        else if (key === 'class') el.className = value;
        else el.setAttribute(key, value);
      });
      root.appendChild(el);
    }
    return el;
  };

  const componentKey = (component) => component.dataset.productId || component.dataset.productHandle || location.pathname;
  const storageKey = (component) => `choices-fitting-room:${location.pathname}:${componentKey(component)}`;

  const openDb = () => new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, 1);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) db.createObjectStore(STORE_NAME, { keyPath: 'key' });
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });

  const withStore = async (mode, runner) => {
    const db = await openDb();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, mode);
      const store = tx.objectStore(STORE_NAME);
      const request = runner(store);
      tx.oncomplete = () => resolve(request?.result);
      tx.onerror = () => reject(tx.error || request?.error);
      tx.onabort = () => reject(tx.error || request?.error);
    });
  };

  const cachePut = (key, blob) => withStore('readwrite', (store) => store.put({ key, blob, updatedAt: Date.now() }));
  const cacheDelete = (key) => withStore('readwrite', (store) => store.delete(key));
  const cacheGetRecord = (key) => withStore('readonly', (store) => store.get(key));
  const cleanupCache = async () => {
    try {
      const now = Date.now();
      const records = await withStore('readonly', (store) => store.getAll());
      (records || []).forEach((record) => {
        if (!record || !record.key) return;
        if ((record.updatedAt || 0) + CACHE_TTL < now) cacheDelete(record.key).catch(() => {});
      });
    } catch (error) {
      console.warn('Choices cache cleanup skipped', error);
    }
  };

  const photoSignature = (component) => {
    if (component.__cgaPhotoSignature) return component.__cgaPhotoSignature;
    const file = component.file;
    if (file) {
      component.__cgaPhotoSignature = [file.name, file.size, file.lastModified].join(':');
    } else {
      component.__cgaPhotoSignature = component.__cgaPhotoSignature || `session:${componentKey(component)}`;
    }
    return component.__cgaPhotoSignature;
  };

  const previewCacheKey = (component, size, view) => ['choices', componentKey(component), photoSignature(component), size, view].join('::');

  const fetchBlob = async (url) => {
    const response = await fetch(url, { credentials: 'omit', mode: 'cors' });
    if (!response.ok) throw new Error(`Preview fetch failed: ${response.status}`);
    return await response.blob();
  };

  const objectUrlFromBlob = (blob) => {
    const url = URL.createObjectURL(blob);
    createdObjectUrls.add(url);
    return url;
  };

  const restoreCachedImage = async (cacheKey) => {
    const record = await cacheGetRecord(cacheKey).catch(() => null);
    if (!record || !record.blob) return '';
    if ((record.updatedAt || 0) + CACHE_TTL < Date.now()) {
      cacheDelete(cacheKey).catch(() => {});
      return '';
    }
    return objectUrlFromBlob(record.blob);
  };

  const serializeEntry = (entry) => {
    if (!entry) return null;
    const output = {
      currentAngle: entry.currentAngle || 'front',
      frontPredictionId: entry.frontPredictionId || ''
    };
    VIEWS.forEach((view) => {
      if (entry[`${view}CacheKey`]) output[`${view}CacheKey`] = entry[`${view}CacheKey`];
      else if (typeof entry[view] === 'string' && entry[view] && !entry[view].startsWith('blob:')) output[view] = entry[view];
    });
    return output;
  };

  const buildPayload = (component) => {
    const fit = component.fitInputs?.find((input) => input.checked)?.value || '';
    const results = {};
    Object.entries(component.results || {}).forEach(([size, entry]) => {
      const serialized = serializeEntry(entry);
      if (serialized) results[size] = serialized;
    });
    return {
      selectedSize: component.selectedSize || '',
      recommendedSize: component.recommendedSize || '',
      currentAngle: component.currentAngle || 'front',
      results,
      usual: component.usual?.value || '',
      feet: component.feet?.value || '',
      inches: component.inches?.value || '',
      chest: component.chest?.value || '',
      chestUnknown: !!component.chestUnknown?.checked,
      fit,
      photoSignature: photoSignature(component),
      savedAt: Date.now()
    };
  };

  const saveSession = (component) => {
    try {
      sessionStorage.setItem(storageKey(component), JSON.stringify(buildPayload(component)));
    } catch (error) {
      console.warn('Choices session save skipped', error);
    }
  };

  const clearSession = (component) => {
    try { sessionStorage.removeItem(storageKey(component)); } catch (error) {}
  };

  const persistGeneratedViews = async (component) => {
    const size = component.selectedSize;
    if (!size) return;
    const entry = component.results?.[size];
    if (!entry) return;

    for (const view of VIEWS) {
      const source = entry[view];
      if (!source || entry[`${view}CacheKey`]) continue;
      try {
        const blob = await fetchBlob(source);
        const key = previewCacheKey(component, size, view);
        await cachePut(key, blob);
        entry[`${view}CacheKey`] = key;
      } catch (error) {
        console.warn(`Choices could not cache ${view} preview`, error);
      }
    }
    saveSession(component);
  };

  const restoreSession = async (component) => {
    let saved;
    try {
      saved = JSON.parse(sessionStorage.getItem(storageKey(component)) || 'null');
    } catch (error) {
      return;
    }
    if (!saved || (saved.savedAt || 0) + (6 * 60 * 60 * 1000) < Date.now()) return;

    if (component.usual) component.usual.value = saved.usual || '';
    if (component.feet) component.feet.value = saved.feet || '';
    if (component.inches) component.inches.value = saved.inches || '';
    if (component.chestUnknown) {
      component.chestUnknown.checked = !!saved.chestUnknown;
      if (component.chest) component.chest.disabled = !!saved.chestUnknown;
    }
    if (component.chest) component.chest.value = saved.chest || '';
    component.fitInputs?.forEach((input) => { input.checked = input.value === saved.fit; });
    component.__cgaPhotoSignature = saved.photoSignature || component.__cgaPhotoSignature;
    component.recommendedSize = saved.recommendedSize || '';
    component.currentAngle = saved.currentAngle || 'front';
    component.results = {};

    const savedResults = saved.results || {};
    for (const [size, data] of Object.entries(savedResults)) {
      const live = {
        currentAngle: data.currentAngle || 'front',
        frontPredictionId: data.frontPredictionId || ''
      };
      for (const view of VIEWS) {
        if (data[`${view}CacheKey`]) {
          live[`${view}CacheKey`] = data[`${view}CacheKey`];
          const objectUrl = await restoreCachedImage(data[`${view}CacheKey`]);
          if (objectUrl) live[view] = objectUrl;
        }
        if (!live[view] && data[view]) live[view] = data[view];
      }
      component.results[size] = live;
    }

    component.updateRecommendation?.();
    if (saved.selectedSize) {
      component.selectSize?.(saved.selectedSize);
      const result = component.results?.[saved.selectedSize];
      if (result?.front) {
        component.currentAngle = result.currentAngle || saved.currentAngle || 'front';
        if (component.resultImage) component.resultImage.src = result[component.currentAngle] || result.front;
        component.resultImage.hidden = false;
        component.show?.('result');
        component.updateAngles?.();
      }
    }
    saveSession(component);
  };

  const syncConsent = (component) => {
    const box = component.querySelector('[data-consent]');
    const button = component.querySelector('[data-generate]');
    if (!box || !button) return;
    button.disabled = !box.checked;
    button.setAttribute('aria-disabled', String(!box.checked));
    button.classList.toggle('is-disabled', !box.checked);
    if (!box.dataset.cgaConsentBound) {
      box.dataset.cgaConsentBound = '1';
      box.addEventListener('change', () => syncConsent(component));
    }
  };

  const buildAngles = (result) => {
    let angles = result.querySelector('.choices-try-on__angles');
    if (!angles) {
      angles = document.createElement('section');
      angles.className = 'choices-try-on__angles';
      const title = document.createElement('p');
      title.className = 'choices-profile__kicker';
      title.textContent = 'VIEW YOUR LOOK';
      const controls = document.createElement('div');
      controls.className = 'choices-try-on__angle-buttons';
      [['front', 'FRONT'], ['three_quarter', '3/4 VIEW'], ['back', 'BACK']].forEach(([value, label]) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.dataset.angle = value;
        button.textContent = label;
        controls.appendChild(button);
      });
      const status = document.createElement('p');
      status.dataset.angleStatus = '';
      status.className = 'choices-try-on__angle-status';
      status.textContent = 'Generate the front view first.';
      angles.append(title, controls, status);
      const actions = result.querySelector('.choices-try-on__result-actions,[data-result-actions]');
      actions ? result.insertBefore(angles, actions) : result.appendChild(angles);
    } else {
      angles.hidden = false;
      if (!angles.querySelector('[data-angle]')) {
        const controls = document.createElement('div');
        controls.className = 'choices-try-on__angle-buttons';
        [['front', 'FRONT'], ['three_quarter', '3/4 VIEW'], ['back', 'BACK']].forEach(([value, label]) => {
          const button = document.createElement('button');
          button.type = 'button';
          button.dataset.angle = value;
          button.textContent = label;
          controls.appendChild(button);
        });
        angles.prepend(controls);
      }
      ensure(angles, '[data-angle-status]', 'p', { 'data-angle-status': '', class: 'choices-try-on__angle-status', text: 'Generate the front view first.' });
    }
  };

  const prepare = () => document.querySelectorAll('choices-try-on').forEach((component) => {
    const open = component.querySelector('[data-try-on-open]');
    const backdrop = component.querySelector('[data-try-on-backdrop]');
    const close = component.querySelector('[data-try-on-close]');
    const mount = component.querySelector('[data-try-on-mount]');
    const productInfo = document.querySelector('.product__info-container');

    const loading = component.querySelector('[data-panel="loading"]');
    if (loading) ensure(loading, '[data-loading-message]', 'p', { 'data-loading-message': '', class: 'choices-try-on__loading-message', text: 'Building your personalized preview…' });

    const result = component.querySelector('[data-panel="result"]');
    if (result) {
      let frame = result.querySelector('.choices-try-on__result-frame');
      if (!frame) {
        const image = result.querySelector('[data-result-image]');
        frame = image?.parentElement || document.createElement('div');
        frame.classList.add('choices-try-on__result-frame');
        if (!frame.parentElement) result.prepend(frame);
      }
      ensure(frame, '[data-result-image]', 'img', { 'data-result-image': '', alt: 'Virtual try-on result' });
      buildAngles(result);
    }

    const review = component.querySelector('[data-panel="review"]');
    if (review) ensure(review, '[data-error]', 'p', { 'data-error': '', class: 'choices-profile__field-error' }).hidden = true;

    if (mount && productInfo) {
      mount.hidden = false;
      mount.style.display = 'block';
      mount.style.width = '100%';
      const anchor = productInfo.querySelector('variant-selects,variant-radios') || productInfo.querySelector('.product-form__quantity') || productInfo.querySelector('.product-form');
      if (anchor) anchor.insertAdjacentElement('afterend', mount);
      else if (!productInfo.contains(mount)) productInfo.appendChild(mount);
    }

    if (open && backdrop && !open.dataset.fb) {
      open.dataset.fb = '1';
      open.addEventListener('click', (event) => {
        event.preventDefault();
        backdrop.hidden = false;
        document.body.classList.add('choices-try-on-open');
      });
    }
    if (close && backdrop && !close.dataset.fb) {
      close.dataset.fb = '1';
      close.addEventListener('click', () => {
        backdrop.hidden = true;
        document.body.classList.remove('choices-try-on-open');
      });
    }

    syncConsent(component);
  });

  document.addEventListener('change', (event) => {
    if (event.target.matches?.('[data-consent]')) {
      const component = event.target.closest('choices-try-on');
      if (component) syncConsent(component);
    }
  }, true);

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', prepare, { once: true });
  else prepare();
  setTimeout(prepare, 200);
  setTimeout(prepare, 800);
  cleanupCache();

  ['choices-fit-profile-v4.css', 'choices-angle-views.css'].forEach((filename) => {
    if (document.querySelector(`link[data-cga="${filename}"]`)) return;
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = assetUrl(filename);
    link.dataset.cga = filename;
    document.head.appendChild(link);
  });

  Promise.all(
    ['choices-core-1.txt', 'choices-core-2.txt', 'choices-core-3.txt'].map((filename) =>
      fetch(assetUrl(filename), { cache: 'no-store' }).then((response) => {
        if (!response.ok) throw new Error(filename);
        return response.text();
      })
    )
  ).then((parts) => {
    prepare();
    const script = document.createElement('script');
    script.textContent = parts.join('');
    document.head.appendChild(script);

    const Klass = customElements.get('choices-try-on');
    if (Klass && !Klass.prototype.__cgaSessionPatched) {
      Klass.prototype.__cgaSessionPatched = true;

      const wrapSync = (name, after) => {
        const original = Klass.prototype[name];
        if (typeof original !== 'function') return;
        Klass.prototype[name] = function(...args) {
          const output = original.apply(this, args);
          try { after.call(this, args, output); } catch (error) { console.warn(`Choices ${name} patch skipped`, error); }
          return output;
        };
      };

      const wrapAsync = (name, after) => {
        const original = Klass.prototype[name];
        if (typeof original !== 'function') return;
        Klass.prototype[name] = async function(...args) {
          const output = await original.apply(this, args);
          try { await after.call(this, args, output); } catch (error) { console.warn(`Choices ${name} patch skipped`, error); }
          return output;
        };
      };

      wrapSync('handleFile', function(args) {
        const file = args?.[0];
        if (file) this.__cgaPhotoSignature = [file.name, file.size, file.lastModified].join(':');
        clearSession(this);
        saveSession(this);
      });

      wrapSync('show', function() { saveSession(this); });
      wrapSync('selectSize', function() { saveSession(this); });
      wrapSync('updateResult', function() { saveSession(this); });

      wrapSync('reset', function() {
        clearSession(this);
      });

      wrapAsync('generate', async function() {
        await persistGeneratedViews(this);
        saveSession(this);
      });

      wrapAsync('angle', async function() {
        await persistGeneratedViews(this);
        saveSession(this);
      });

      wrapAsync('addToCart', async function() {
        saveSession(this);
      });
    }

    setTimeout(() => {
      prepare();
      document.querySelectorAll('choices-try-on').forEach((component) => { restoreSession(component); });
    }, 150);
  }).catch((error) => console.error('Choices fitting room failed to load', error));

  window.addEventListener('pagehide', () => {
    document.querySelectorAll('choices-try-on').forEach((component) => saveSession(component));
  });

  window.addEventListener('pageshow', () => {
    prepare();
    setTimeout(() => document.querySelectorAll('choices-try-on').forEach((component) => restoreSession(component)), 80);
  });

  window.addEventListener('beforeunload', () => {
    createdObjectUrls.forEach((url) => URL.revokeObjectURL(url));
    createdObjectUrls.clear();
  });
})();
